import React, { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createMailCredential,
  fetchMailCredentials,
  revokeMailCredential,
  type CreatedMailCredential,
  type MailCredentialsResponse,
} from '@/api/mailCredentialsApi';
import styles from './MailClients.module.scss';

export const MailClients: React.FC = () => {
  const { t } = useTranslation('mail');
  const [data, setData] = useState<MailCredentialsResponse>();
  const [description, setDescription] = useState('');
  const [created, setCreated] = useState<CreatedMailCredential>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setError('');
      setData(await fetchMailCredentials());
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        window.location.assign('/auth/login');
        return;
      }
      setError(err instanceof Error ? err.message : t('genericError'));
    }
  };

  useEffect(() => { void load(); }, []);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setCreated(undefined);
    try {
      const credential = await createMailCredential(description);
      setCreated(credential);
      setDescription('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('genericError'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm(t('confirmRevoke'))) return;
    setBusy(true);
    try {
      await revokeMailCredential(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('genericError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={styles.main}>
      <header className={styles.heading}>
        <p className={styles.eyebrow}>{t('eyebrow')}</p>
        <h1>{t('title')}</h1>
        <p>{t('intro')}</p>
      </header>

      {error && <div className={styles.error} role="alert">{error}</div>}

      {data && (
        <>
          <section className={styles.card}>
            <h2>{t('settingsTitle')}</h2>
            <dl className={styles.settings}>
              <div><dt>{t('username')}</dt><dd>{data.settings.address}</dd></div>
              <div><dt>{t('imap')}</dt><dd>{data.settings.imapHost}:{data.settings.imapPort} · SSL/TLS</dd></div>
              <div><dt>{t('smtp')}</dt><dd>{data.settings.smtpHost}:{data.settings.smtpPort} · STARTTLS</dd></div>
            </dl>
          </section>

          <section className={styles.card}>
            <h2>{t('createTitle')}</h2>
            <p>{t('createHelp')}</p>
            <form className={styles.form} onSubmit={create}>
              <label htmlFor="credential-description">{t('description')}</label>
              <div className={styles.formRow}>
                <input id="credential-description" value={description} maxLength={64} required
                  placeholder={t('descriptionPlaceholder')} onChange={(event) => setDescription(event.target.value)} />
                <button type="submit" disabled={busy}>{busy ? t('working') : t('create')}</button>
              </div>
            </form>
            {created && (
              <div className={styles.secret} role="status">
                <strong>{t('secretTitle')}</strong>
                <code>{created.secret}</code>
                <button type="button" onClick={() => void navigator.clipboard.writeText(created.secret)}>{t('copy')}</button>
                <p>{t('secretWarning')}</p>
              </div>
            )}
          </section>

          <section className={styles.card}>
            <h2>{t('existingTitle')}</h2>
            {data.credentials.length === 0 ? <p>{t('empty')}</p> : (
              <ul className={styles.credentials}>
                {data.credentials.map((credential) => (
                  <li key={credential.id}>
                    <div><strong>{credential.description}</strong>{credential.expiresAt && <span>{t('expires', { date: new Date(credential.expiresAt).toLocaleDateString() })}</span>}</div>
                    <button type="button" disabled={busy} onClick={() => void revoke(credential.id)}>{t('revoke')}</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
};
